import React, { FC, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Input } from 'reactstrap';

type Props = {
  value?: number;
  onChange: (value: number) => void;
  onValidChange?: (validationMessage?: string) => void;
  required?: boolean;
  min?: number;
  max?: number;
  step?: number;
  minLabel?: string;
  maxLabel?: string;
  showLabels?: boolean;
  className?: string;
  disabled?: boolean;
};

const SliderInput: FC<Props> = ({
  value,
  onChange,
  onValidChange,
  required,
  min = 1,
  max = 10,
  step = 1,
  showLabels,
  minLabel,
  maxLabel,
  className,
  disabled,
}) => {
  const { formatMessage } = useIntl();

  const [validationMessage, setValidationMessage] = useState<
    string | undefined
  >(undefined);

  useEffect(() => {
    if (required) {
      const isValid = value != null;
      setValidationMessage(
        isValid
          ? undefined
          : formatMessage({
              id: 'app.widgets.inputs.slider_input.please_select_a_value',
              defaultMessage: 'Please select a value.',
            })
      );
    } else {
      setValidationMessage(undefined);
    }
  }, [value, required, formatMessage]);

  useEffect(() => {
    if (onValidChange) {
      onValidChange(validationMessage);
    }
  }, [validationMessage, onValidChange]);

  const handleOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = parseFloat(event.target.value);
      onChange(newValue);
    },
    [onChange]
  );

  const currentValue = value ?? min;

  return (
    <div className={className}>
      <div className="d-flex align-items-center gap-3">
        <Input
          type="range"
          min={min}
          max={max}
          step={step}
          value={currentValue}
          onChange={handleOnChange}
          disabled={disabled}
          className="form-range flex-grow-1"
        />
      </div>
      {!!showLabels && (
        <div className="d-flex justify-content-between small text-muted mt-1">
          <span>{minLabel ?? min}</span>
          <span>{maxLabel ?? max}</span>
        </div>
      )}
    </div>
  );
};

export default React.memo(SliderInput);
